import { Check, Search, Timer, X } from 'lucide-react'
import { FC, useEffect } from 'react'

import apps, { appIcons, appNames } from '@/apps'
import ShortenedRef from '@/components/global/shortened-ref'
import RelativeDate from '@/components/relative-date'
import { Button } from '@/components/ui/button'
import { Command, CommandDialog, CommandEmpty, CommandInput, CommandList } from '@/components/ui/command'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useGlobalSearch } from '@/hooks/useGlobalSearch'
import { cn } from '@/lib/utils'
import formatPhoneNumber from '@/util/format-phone-number'

import FilterStatus from './FilterStatus'
import SearchFilters from './SearchFilters'

const GlobalSearch: FC = () => {
  const {
    results,
    unfilteredResults,
    isLoading,
    isOpen,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    handleOpenChange,
  } = useGlobalSearch()

  const handleInputChange = (value: string) => {
    setSearchQuery(value)
  }

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        handleOpenChange(!isOpen)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [isOpen, handleOpenChange])

  return (
    <>
      <Button
        variant="outline"
        className="justify-between gap-8 text-sm text-muted-foreground md:min-w-52"
        onClick={() => handleOpenChange(true)}
        data-testid="global-search-field"
      >
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4" />
          <span className="inline-flex">Search customer...</span>
        </div>
        <kbd className="h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        className={cn(
          'w-full',
          unfilteredResults.length > 0 && searchQuery !== '' && 'max-w-5xl',
          results.length === 0 && 'min-w-5xl'
        )}
      >
        <Command>
          <CommandInput
            placeholder="Search customer across all apps..."
            value={searchQuery}
            onValueChange={handleInputChange}
            className="border-none outline-none focus:ring-0"
            data-testid="global-search-input"
          />
          {unfilteredResults.length > 0 && (
            <SearchFilters filters={filters} setFilters={setFilters}>
              <FilterStatus totalResults={unfilteredResults.length} filteredResults={results.length} />
            </SearchFilters>
          )}
          <CommandList className="w-full max-h-[70vh]">
            {searchQuery && searchQuery.trim() ? (
              isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-10 h-10 border-4 rounded-full border-primary border-t-transparent animate-spin" />
                </div>
              ) : results.length === 0 ? (
                <CommandEmpty>
                  No results found for &quot;{searchQuery}&quot;.
                  <p className="mt-1 text-sm text-muted-foreground">Try searching for a different customer ID.</p>
                </CommandEmpty>
              ) : (
                <>
                  <Table className="overflow-hidden" parentClassName="border-none">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-sm font-semibold">Date</TableHead>
                        <TableHead className="text-sm font-semibold">App</TableHead>
                        <TableHead className="text-sm font-semibold">Status</TableHead>
                        <TableHead className="text-sm font-semibold">Session</TableHead>
                        <TableHead className="text-sm font-semibold">Phone Number</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.map(result => (
                        <TableRow key={result.id} className="transition-colors duration-200 hover:bg-muted/50">
                          <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                            <RelativeDate date={result.timestamp} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2.5">
                              {(() => {
                                const Icon = appIcons[result.app]
                                const colorKey = apps.find(a => a.id === result.app)?.color || 'blue'
                                return (
                                  <span
                                    className={`inline-flex items-center justify-center rounded h-8 w-8 text-${colorKey}-600 bg-${colorKey}-50`}
                                  >
                                    <Icon className="w-4 h-4" />
                                  </span>
                                )
                              })()}
                              <span className="text-sm font-medium">{appNames[result.app]}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <span
                                className={cn(
                                  result.status === 'success'
                                    ? 'bg-emerald-500/15 hover:bg-emerald-500/15 text-emerald-600'
                                    : result.status === 'failure'
                                      ? 'bg-rose-500/15 hover:bg-rose-500/15 text-rose-600'
                                      : 'bg-slate-500/15 hover:bg-slate-500/15 text-slate-600',
                                  'inline-flex items-center justify-center rounded h-8 w-8'
                                )}
                              >
                                {result.status === 'success' ? (
                                  <Check className="w-4 h-4" />
                                ) : result.status === 'failure' ? (
                                  <X className="w-4 h-4" />
                                ) : (
                                  <Timer className="w-4 h-4" />
                                )}
                              </span>
                              <span>
                                {result.status === 'success'
                                  ? 'Successful'
                                  : result.status === 'failure'
                                    ? 'Failed'
                                    : 'Timed out'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {result.sessionRef && (
                              <div className="flex items-center gap-2 font-mono text-xs">
                                <ShortenedRef>{result.sessionRef}</ShortenedRef>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {result.phoneNumber && (
                              <div className="flex items-center text-sm whitespace-nowrap">
                                {formatPhoneNumber(result.phoneNumber)}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )
            ) : (
              <CommandEmpty>
                Enter a customer ID to see activity across all apps.
                <p className="mt-1 text-sm text-muted-foreground">
                  Results will include all apps and interactions for the customer.
                </p>
              </CommandEmpty>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  )
}

export default GlobalSearch
